import { useState, useEffect, useCallback } from "react";

interface LoadingScreenProps {
  onComplete: () => void;
}

const SCRAMBLE_CHARS =
  "!@#$%^&*()_+-=[]{}|;':\",./<>?0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const LoadingScreen = ({ onComplete }: LoadingScreenProps) => {
  const [phase, setPhase] = useState<"fade-in" | "scramble" | "fade-out">(
    "fade-in",
  );
  const [displayWord, setDisplayWord] = useState("");
  const [opacity, setOpacity] = useState(0);

  const scrambleWord = useCallback((targetPhrase: string, duration: number) => {
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      if (progress < 1) {
        // Generate scrambled text with gradual reveal
        let result = "";
        for (let i = 0; i < targetPhrase.length; i++) {
          if (targetPhrase[i] === " ") {
            result += " ";
          } else if (progress > (i + 1) / targetPhrase.length) {
            result += targetPhrase[i];
          } else {
            result +=
              SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
          }
        }
        setDisplayWord(result);
        requestAnimationFrame(animate);
      } else {
        setDisplayWord(targetPhrase);
      }
    };

    requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    // Phase 1: Fade in
    const fadeInTimer = setTimeout(() => {
      setOpacity(1);
    }, 100);

    // Phase 2: Start scramble after text is visible
    const scrambleTimer = setTimeout(() => {
      setPhase("scramble");
      scrambleWord("but not anymore", 800);
    }, 1200);

    // Phase 3: Fade out
    const fadeOutTimer = setTimeout(() => {
      setPhase("fade-out");
      setOpacity(0);
    }, 2000);

    // Complete
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2500);

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(scrambleTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, scrambleWord]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-white transition-opacity duration-700"
      style={{ opacity: phase === "fade-out" ? 0 : 1 }}
    >
      {/* Retro Grid Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Horizontal lines */}
        <div className="absolute inset-0">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={`h-${i}`}
              className="absolute left-0 right-0 h-px bg-gray-200"
              style={{ top: `${(i + 1) * 5}%` }}
            />
          ))}
        </div>
        {/* Vertical lines */}
        <div className="absolute inset-0">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={`v-${i}`}
              className="absolute top-0 bottom-0 w-px bg-gray-200"
              style={{ left: `${(i + 1) * 5}%` }}
            />
          ))}
        </div>
        <div
          className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-gray-100/50"
          style={{
            animation: "pulse 2s ease-in-out infinite",
          }}
        />
      </div>

      {/* Text Content */}
      <div
        className="relative z-10 text-center transition-opacity duration-1000"
        style={{ opacity }}
      >
        <h1 className="text-4xl md:text-6xl font-light tracking-tight text-gray-900">
          Privacy is myth{" "}
          <span
            className={`inline-block font-semibold transition-all duration-300 ${
              phase === "scramble" || displayWord === "but not anymore"
                ? "text-green-600"
                : "text-gray-900"
            }`}
            style={{
              fontFamily: "monospace",
              minWidth: "16ch",
            }}
          >
            {displayWord}
          </span>
        </h1>
        <p className="mt-4 text-sm text-gray-500 tracking-widest uppercase">
          PDFWorkSpace
        </p>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-8 left-8 w-12 h-12 border-l-2 border-t-2 border-gray-300" />
      <div className="absolute top-8 right-8 w-12 h-12 border-r-2 border-t-2 border-gray-300" />
      <div className="absolute bottom-8 left-8 w-12 h-12 border-l-2 border-b-2 border-gray-300" />
      <div className="absolute bottom-8 right-8 w-12 h-12 border-r-2 border-b-2 border-gray-300" />
    </div>
  );
};

export default LoadingScreen;
